import { useFormContext } from 'react-hook-form';
import type { AdsHistory } from '../types';
import { usePagination } from 'hooks/usePagination';
import { useAdsHistory } from '../api';
import { useAdsHistoryColumns, useGridSelection } from '../hooks';
import { BaseGrid, SuspenseBoundary } from 'components/common';
import { useListParams } from 'hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import type { MarketingProcess } from 'types/shared';

interface AdsHistoryGridProps {
  marketingProcess?: MarketingProcess;
}

const AdsHistoryGridComponent = ({ marketingProcess }: AdsHistoryGridProps) => {
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const {
    desc,
    orderBy,
    setQ,
    setStatus,
    setDate,
    q: urlQ,
    status: urlStatus,
    date: urlDate,
  } = useListParams();
  const [debouncedFilters, setDebouncedFilters] = useState({
    q: urlQ ?? '',
    status: urlStatus ?? '',
    date: urlDate ?? '',
  });

  const filterByQ = watch('q') as string;
  const filterStatus = watch('status') as string;
  const filterDate = watch('date') as string;

  const debouncedUpdate = useCallback(
    (q: string, status: string, date: string) => {
      setDebouncedFilters({ q, status, date });
      setQ(q || undefined);
      setStatus(status || undefined);
      setDate(date || undefined);
    },
    [setDebouncedFilters, setQ, setStatus, setDate]
  );

  const debouncedFn = useMemo(
    () => debounce(debouncedUpdate, 500),
    [debouncedUpdate]
  );

  useEffect(() => {
    debouncedFn(filterByQ, filterStatus, filterDate);
    return () => {
      debouncedFn.cancel();
    };
  }, [filterByQ, filterStatus, filterDate, debouncedFn]);

  const { selectedRows, handleSelectionChange } =
    useGridSelection('selectedAdsHistory');

  const columns = useAdsHistoryColumns();

  const {
    data: { data: adsHistory, count },
    isLoading,
  } = useAdsHistory({
    params: {
      ...(debouncedFilters.q && { name: debouncedFilters.q }),
      ...(debouncedFilters.status && {
        status: debouncedFilters.status === 'success',
      }),
      ...(debouncedFilters.date && {
        start_date: debouncedFilters.date.split(' - ')[0] ?? undefined,
      }),
      ...(debouncedFilters.date && {
        end_date: debouncedFilters.date.split(' - ')[1] ?? undefined,
      }),
      ...(marketingProcess && { marketing_process: marketingProcess }),
      desc,
      order_by: orderBy,
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const emptyPlaceholder =
    marketingProcess === 'scaling'
      ? 'No Scaling Ads posted recently.'
      : marketingProcess === 'testing'
        ? 'No Testing Ads posted recently.'
        : 'No Ads posted recently.';

  return (
    <BaseGrid<AdsHistory>
      rows={adsHistory}
      columns={columns}
      count={count}
      isLoading={isLoading}
      pagination={pagination}
      rowSelectionModel={selectedRows}
      onRowSelectionModelChange={handleSelectionChange}
      getRowId={(row) => row.created_at}
      emptyPlaceholder={emptyPlaceholder}
    />
  );
};

const AdsHistoryGrid = ({ marketingProcess }: AdsHistoryGridProps) => {
  return (
    <SuspenseBoundary>
      <AdsHistoryGridComponent marketingProcess={marketingProcess} />
    </SuspenseBoundary>
  );
};

export default AdsHistoryGrid;
